import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";
import SimulationResults from "../components/console/SimulationResults";
import { ContainerIcon } from "../components/shared/ContainerIcon";
import { ScrollableList } from "../components/shared/ScrollableList";
import { logEvent } from "../utils/analytics";

// lengthFt/highCube aren't stored on entities.containers (just length/width/
// height in meters) — derived here instead of adding fields to the data,
// since ContainerIcon's own sizing is already bucketed (<=20/<=40/>40) and
// tolerant of any numeric lengthFt, standard or custom.
const FT_PER_M = 0.3048;
function iconPropsFor(c) {
  return { lengthFt: Math.round(c.length / FT_PER_M), highCube: c.height > 2.5 };
}

// Always-visible "what's being simulated" column — deliberately not the
// hover popover Beyond CBM/console use for their editable inputs, since
// nothing here is editable and the user should see it without interacting.
// Built straight from results.entities (the same containers/items/pallets
// the algorithm actually ran on), not a separately-stored input payload.
function ExampleInputsPanel({ example }) {
  const { containers, items, pallets } = example.results.entities;
  return (
    <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
        <span className="material-symbols-outlined text-sm">input</span>
        What's being simulated
      </p>
      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Containers{example.handlingType === "palletized" && <span className="text-primary"> · palletized</span>}
        </p>
        <ScrollableList maxHeightClass="max-h-72" innerClassName="space-y-1.5">
          {Object.entries(containers).map(([name, c]) => (
            <div key={name} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <ContainerIcon {...iconPropsFor(c)} />
              <div>
                <p className="text-xs font-bold text-white">{name}</p>
                <p className="text-[11px] text-slate-400">{c.length}×{c.width}×{c.height}m · ${c.cost.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </ScrollableList>
      </div>
      {pallets && Object.keys(pallets).length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Pallets</p>
          <div className="space-y-1.5">
            {Object.entries(pallets).map(([name, p]) => (
              <div key={name} className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
                <p className="text-xs font-bold text-white">{name}</p>
                <p className="text-[11px] text-slate-400">{p.length}×{p.width}×{p.height}m · max {p.maxWeightAllowed}kg</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Items</p>
        <ScrollableList maxHeightClass="max-h-52" innerClassName="space-y-1.5">
          {Object.entries(items).map(([name, it]) => (
            <div key={name} className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-2">
              <p className="text-xs font-bold text-white">
                {name} <span className="font-normal text-slate-500">×{it.quantity}</span>
              </p>
              <p className="text-[11px] text-slate-400">{it.length}×{it.width}×{it.height}m · {it.weight}kg</p>
            </div>
          ))}
        </ScrollableList>
      </div>

      {/* Catchy, per-example CTA — tailored copy per shipment type, not one
          generic line repeated across all 7 tabs. Reuses the hero button's
          shimmering gradient (.animate-gradient) so it reads as the site's
          established "this is the action" treatment, not a new one-off. */}
      <Link
        to="/3d-cargo-load-simulator"
        onClick={() => logEvent({ name: "examples_inline_cta", type: "cta", meta: { slug: example.slug } })}
        className="group flex items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-primary via-blue-500 to-cyan-500 bg-[length:200%_200%] animate-gradient px-3.5 py-3 text-xs font-bold leading-snug text-white shadow-lg shadow-primary/30 transition-all hover:shadow-xl hover:shadow-primary/50 hover:scale-[1.02] active:scale-95"
      >
        <span>{example.cta}</span>
        <span className="material-symbols-outlined shrink-0 text-base transition-transform group-hover:translate-x-1">arrow_forward</span>
      </Link>
    </div>
  );
}

// Static, pre-computed examples — served from public/examples-data.json (a
// plain fetch, not an API route), generated once by running the real
// packing engine offline. No simulation call happens on this page at all;
// every 3D scene here is frozen algorithm output. Only the 3D arena itself
// is interactive (orbit/zoom via SimulationResults' own viewer) — nothing
// on this page is editable, unlike Beyond CBM's live free-tool.
export function ExamplesPage() {
  const [examples, setExamples] = useState(null);
  const [error, setError] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);

  useEffect(() => {
    fetch("/examples-data.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load examples");
        return res.json();
      })
      .then((data) => {
        setExamples(data);
        setActiveSlug(data[0]?.slug);
      })
      .catch(() => setError(true));
  }, []);

  const active = examples?.find((e) => e.slug === activeSlug);

  const selectTab = (ex) => {
    setActiveSlug(ex.slug);
    logEvent({ name: "examples_tab_switch", type: "click", meta: { slug: ex.slug } });
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-dark text-white">
      <Helmet>
        <title>Explore Examples — Real 3D Container Packing Simulations | Feuteuristix</title>
        <meta name="description" content="Seven real container-load simulations, pre-run and ready to explore in 3D — electronics, e-commerce, pharma, batteries, heavy machinery, palletized cargo, and full-fleet cost optimization." />
        <meta property="og:title" content="Explore Examples — Real 3D Container Packing Simulations | Feuteuristix" />
        <meta property="og:description" content="Seven real container-load simulations, pre-run and ready to explore in 3D — electronics, e-commerce, pharma, batteries, heavy machinery, palletized cargo, and full-fleet cost optimization." />
        <meta property="og:url" content="https://feuteuristix.com/examples" />
        <meta name="twitter:title" content="Explore Examples — Real 3D Container Packing Simulations | Feuteuristix" />
        <meta name="twitter:description" content="Seven real container-load simulations, pre-run and ready to explore in 3D — electronics, e-commerce, pharma, batteries, heavy machinery, palletized cargo, and full-fleet cost optimization." />
        <link rel="canonical" href="https://feuteuristix.com/examples" />
      </Helmet>
      <SiteHeader />

      <main className="flex-1 pt-[76.8px] pb-16">
        <section className="bg-gradient-to-b from-background-dark to-slate-950/60 py-7">
          <div className="mx-auto max-w-4xl px-6 md:px-8 text-center">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-primary">Play arena</p>
            <h1 className="mt-1.5 text-2xl font-black leading-tight md:text-3xl">
              See the engine{" "}
              <span className="bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                actually work
              </span>
            </h1>
            <p className="mt-2 text-sm text-slate-300 max-w-2xl mx-auto md:text-base">
              Seven real shipments, already simulated. Spin them, zoom in, see exactly how the packing plan holds up — no inputs to fill, just the results.
            </p>
          </div>
        </section>

        <section className="px-4 pt-8 md:px-8">
          <div className="mx-auto max-w-7xl">
            {error && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-center text-red-300">
                Couldn't load the examples right now. Try refreshing the page.
              </div>
            )}

            {!error && !examples && (
              <div className="flex items-center justify-center py-24 text-slate-400">
                <span className="material-symbols-outlined animate-spin text-3xl">progress_activity</span>
              </div>
            )}

            {examples && active && (
              <>
                {/* Horizontal tabs — flex-wrap (not overflow-x-auto) so they
                    never require a scroll; tight padding/type keeps all 6 on
                    one row at typical desktop widths, wrapping only if the
                    viewport is genuinely too narrow. */}
                <div className="flex flex-wrap gap-1.5" id="examples-tabs">
                  {examples.map((ex) => {
                    const isActive = ex.slug === activeSlug;
                    return (
                      <button
                        key={ex.slug}
                        onClick={() => selectTab(ex)}
                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left transition ${
                          isActive
                            ? "border-primary/60 bg-primary/15 shadow-md shadow-primary/20"
                            : "border-white/10 bg-white/5 hover:border-primary/40 hover:bg-white/10"
                        }`}
                      >
                        <span className={`material-symbols-outlined text-base ${isActive ? "text-primary" : "text-slate-400"}`}>
                          {ex.icon}
                        </span>
                        <span className={`whitespace-nowrap text-xs font-bold ${isActive ? "text-white" : "text-slate-300"}`}>
                          {ex.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* USP callout — the punchy "why this is the smart answer"
                    line, distinct from the softer tagline/description. */}
                <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-lg text-primary">insights</span>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">{active.tagline}</p>
                      <p className="mt-1 text-sm font-semibold text-white">{active.usp}</p>
                      <p className="mt-1 text-xs text-slate-400">{active.description}</p>
                    </div>
                  </div>
                </div>

                {/* Left = inputs (always visible — not a hover popover, so the
                    user actually sees what was fed in before looking at the
                    result), right = the 3D viewer, via SimulationResults'
                    sidePanel prop. No inputContainers/inputPackages/editPanel
                    passed, so the hover "Edit & rerun" affordance never
                    renders — this page is view-only except the 3D itself. */}
                <div className="mt-6">
                  <SimulationResults
                    results={active.results}
                    cameraZoom={0.55}
                    cameraRotationDeg={12}
                    cameraTiltDeg={6}
                    cameraPanRight={0.16}
                    edgeColor="#0d7ff2"
                    canvasHeight={560}
                    showPackageFilter
                    showLegend
                    sidePanel={<ExampleInputsPanel example={active} />}
                  />
                </div>
              </>
            )}
          </div>
        </section>

        {/* Bottom CTA — same treatment as Beyond CBM's closing conversion block */}
        <section className="mx-auto max-w-4xl px-6 md:px-8 pt-14 pb-8">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-lg font-black text-white">Ready to run your own?</p>
              <p className="mt-1 text-sm text-slate-400 max-w-md">
                These are pre-built examples. Run a real simulation on your own cargo — free, no card, no signup needed to try it.
              </p>
            </div>
            <Link
              to="/3d-cargo-load-simulator"
              onClick={() => logEvent({ name: "examples_bottom_cta", type: "cta" })}
              className="shrink-0 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-6 py-3 text-sm font-bold text-white transition hover:brightness-110"
            >
              Run your own simulation
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
